import { Flex, Form } from 'antd';
import { Button, Checkbox, Drawer, Input, Modal, Spinner } from 'ui';
import { useIntl } from 'react-intl';
import { StyledBusinessTab, StyledContractCards } from '../style';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import { useAuth } from 'hooks/useAuth';
import { permissions } from 'config/config';
import React, { useState } from 'react';
import parse from 'html-react-parser';
import { useForm } from 'antd/es/form/Form';
import { useBreakpoint } from 'utils/useBreakpoint';

interface ContractProps {
  id: number;
  contract_type: string;
  contract_value: string;
  contract_nr: string;
  contract_date: string;
  bank_account: string;
  type: string;
}

export const Contracts = ({ farmId }: { farmId: number | string }) => {
  const [contractInfo, setContractInfo] = useState<any>();
  const [agreeToRules, setAgreeToRules] = useState<boolean>(false);
  const [isLearnMore, setIsLearnMore] = useState<boolean>(false);
  const [isConfirmModal, setIsConfirmModal] = useState<string>('');
  const { isLg } = useBreakpoint();

  const intl = useIntl();
  const { user, can } = useAuth();
  const canEditContract = can(permissions.edit_contract);
  const [form] = useForm();

  const { data, isLoading } = useQueryApiClient({
    request: {
      url: `api/v2/farm/${farmId}/contracts`,
    },
    onSuccess: (response) => {
      response.forEach((contract: ContractProps) => {
        form.setFieldsValue({
          [`bank_account_${contract.type}`]: contract.bank_account,
        });
      });
    },
  });

  const { appendData, isLoading: isLoadingClassifier } = useQueryApiClient({
    request: {
      url: `api/v2/classifiers/type/CONTRACT/:code`,
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setContractInfo(response);
    },
  });

  const { appendData: confirmContract } = useQueryApiClient({
    request: {
      url: `api/v2/farm/${farmId}/contracts/:code`,
      method: 'POST',
    },
    onSuccess: () => onDrawerClose(true),
  });

  const { appendData: deleteContract } = useQueryApiClient({
    request: {
      url: `api/v2/farm/${farmId}/contracts/:code`,
      method: 'DELETE',
    },
    onSuccess: () => onDrawerClose(true),
  });

  const { appendData: updateBankAccount, isLoading: isUpdatingBank } =
    useQueryApiClient({
      request: {
        url: `api/v2/farm/${farmId}/contracts/:id`,
        method: 'PATCH',
      },
      onSuccess: () => onDrawerClose(),
    });

  const contractTypes = ['E-MARKET', 'WHOLESALE', 'DYNAMIC'];

  const onDrawerClose = (resetPage = false) => {
    setContractInfo(null);
    setIsLearnMore(false);

    if (resetPage) {
      window.location.reload();
    }
  };

  const onComplete = async (type: string) => {
    try {
      if (type === 'DYNAMIC') {
        confirmContract([], { code: type });
        return;
      }

      if (type === 'E-MARKET' || type === 'WHOLESALE') {
        await form.validateFields([`bank_account_${type}`]);
      }

      appendData([], { code: type });
    } catch (e) {}
  };

  return (
    <StyledBusinessTab>
      <Form form={form}>
        <Spinner spinning={isLoadingClassifier || isLoading}>
          <StyledContractCards>
            {contractTypes.map((type) => {
              const contract = data.find(
                (contract: ContractProps) => contract.contract_type === type
              );
              const hasContract = !!contract?.id;

              if (type === 'DYNAMIC' && !user?.isAdmin && !hasContract) {
                return <></>;
              }

              return (
                <div className={'contract-card'}>
                  <div className="title">
                    {intl.formatMessage({
                      id: `contracts.${type}`,
                    })}
                  </div>
                  <div className={'contract-content'}>
                    {hasContract && (
                      <div className="content-block">
                        <div className={'content-entry'}>
                          <div>
                            {intl.formatMessage({
                              id: 'contracts.contract_nr',
                            })}
                            :
                          </div>
                          <div>{contract.contract_nr}</div>
                        </div>
                        <div className={'content-entry'}>
                          <div>
                            {intl.formatMessage({
                              id: 'contracts.contract_date',
                            })}
                            :
                          </div>
                          <div>{contract.contract_date}</div>
                        </div>
                      </div>
                    )}
                    {(type === 'E-MARKET' || type === 'WHOLESALE') && (
                      <Flex gap={16}>
                        <Input
                          placeholder={intl.formatMessage({
                            id: 'contracts.bank_account_number_full',
                          })}
                          name={`bank_account_${type}`}
                          rules={[
                            {
                              required: true,
                              message: intl.formatMessage({
                                id: 'contracts.bank_account_number_required',
                              }),
                            },
                          ]}
                          className="bank-input"
                          disabled={!canEditContract}
                        />
                        {hasContract && (
                          <Button
                            label={intl.formatMessage({ id: 'general.save' })}
                            loading={isUpdatingBank}
                            onClick={() =>
                              updateBankAccount(
                                {
                                  bank_account: form.getFieldValue(
                                    `bank_account_${type}`
                                  ),
                                },
                                { id: contract.id }
                              )
                            }
                            ability={'edit_contract'}
                          />
                        )}
                      </Flex>
                    )}
                  </div>
                  <div className="contract-buttons">
                    <Button
                      label={intl.formatMessage({
                        id: `contracts.${hasContract ? 'cancel_contract' : 'conclude_contract'}`,
                      })}
                      type="primary"
                      className={
                        type === 'DYNAMIC' ? 'contract-button-margin' : ''
                      }
                      onClick={() => {
                        if (!hasContract) {
                          onComplete(type);
                          return;
                        }

                        setIsConfirmModal(type);
                      }}
                      ability={'edit_contract'}
                    />
                    {type !== 'DYNAMIC' && (
                      <Button
                        label={intl.formatMessage({
                          id: `contracts.${hasContract ? 'contract_rules' : 'know_more'}`,
                        })}
                        type="link"
                        onClick={() => {
                          if (hasContract) {
                            setContractInfo(contract);
                            return;
                          }

                          setIsLearnMore(true);
                          appendData([], { code: type });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </StyledContractCards>
        </Spinner>
      </Form>

      <Drawer
        open={!!contractInfo}
        title={intl.formatMessage({
          id: `contracts.${contractInfo?.code ?? contractInfo?.contract_type}`,
        })}
        onClose={() => onDrawerClose()}
        width={isLg ? '50%' : '100%'}
        footer={
          !contractInfo?.contract_type && (
            <Flex justify={'space-between'} align={'center'}>
              {!isLearnMore && (
                <div>
                  <Checkbox
                    label={intl.formatMessage({
                      id: 'contracts.agree_to_rules',
                    })}
                    checked={agreeToRules}
                    onChange={() => setAgreeToRules((old) => !old)}
                  />
                </div>
              )}

              <Flex gap={10}>
                {!isLearnMore && (
                  <>
                    <Button
                      label={intl.formatMessage({ id: 'general.cancel' })}
                      onClick={onDrawerClose}
                    />
                    <Button
                      type="primary"
                      label={intl.formatMessage({
                        id: 'contracts.conclude_contract',
                      })}
                      disabled={!agreeToRules}
                      onClick={() =>
                        confirmContract(
                          {
                            bank_account: form.getFieldValue(
                              `bank_account_${contractInfo.code ?? contractInfo.contract_type}`
                            ),
                          },
                          {
                            code:
                              contractInfo.code ?? contractInfo.contract_type,
                          }
                        )
                      }
                    />
                  </>
                )}
              </Flex>
            </Flex>
          )
        }
      >
        {(contractInfo?.contract_value || contractInfo?.value) &&
          parse(contractInfo?.contract_value || contractInfo?.value)}
      </Drawer>

      <Modal
        open={!!isConfirmModal}
        onCancel={() => setIsConfirmModal('')}
        footer={
          <Flex gap={10} justify="end">
            <Button
              label={intl.formatMessage({ id: 'general.cancel' })}
              onClick={() => setIsConfirmModal('')}
            />
            <Button
              label={intl.formatMessage({ id: 'general.confirm' })}
              type="primary"
              htmlType="submit"
              onClick={() => deleteContract([], { code: isConfirmModal })}
            />
          </Flex>
        }
      >
        {intl.formatMessage({
          id: 'contracts.confirm_delete',
        })}
      </Modal>
    </StyledBusinessTab>
  );
};
